import Banner from 'components/ui/Banner';
import Card from 'components/ui/Card';
import InsertionIndicator from 'components/ui/InsertionIndicator';
import { useMemo, useState, useEffect, useRef } from 'react';
import { useAppDispatch } from 'store/hooks';
import { t } from 'services/i18n.service';
import HabitActionBar from './HabitActionBar';
import HabitDurationCompletion from './HabitDurationCompletion';
import HabitNameInput from './HabitNameInput';
import HabitFeatureTags from './HabitFeatureTags';
import Container from 'components/ui/Container';
import BaseButton from 'components/ui/BaseButton';
import Draggable from 'assets/icons/Draggable';
import COLOR from 'constants/color';
import { ActivityType } from 'interfaces/common.interface';
import {
  updateSettingFeatures,
  insertNewHabit,
  updateMoreOptions,
  updateIsShowMoreOptionsOpened,
  openUnlockHabitPackModal,
  cloneHabit
} from 'store/reducer/setting/slice';
import {
  updateMoreOptionActivity,
  updateMoreOptionActiveTab
} from 'store/reducer/modal/slice';
import { EMPTY_STRING, HABIT_SETTING_FEATURES } from 'constants/general';
import useConfirmSettingsDeletion from 'hooks/useConfirmSettingsDeletion';
import useHabitRowTimerMode from 'hooks/useHabitRowTimerMode';
import { getActivityHabitFieldErrorKeys } from 'utils/habit-edit-form.util';
import { hasCompletionRequirement } from 'utils/validators';
import { getHabitRowDataProps } from 'utils/validation-error-navigation.util';
import {
  MORE_OPTION_REVAMP_TAB,
  SETTINGS_DELETE_TARGET,
  SUGGESTED_PACK
} from 'enums';
import { DraggableAttributes } from '@dnd-kit/core';
import { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities';
import { ExtendedSuggestion } from 'interfaces/setting.interface';
import { increment } from 'utils/helpers';
import { getHabitAccessibleName } from 'utils/habit-accessibility.util';

interface TabBodyRowRevampProps {
  activity: ActivityType;
  position: number;
  type: string;
  sequence_id: string;
  customRoutinePosition?: number;
  dragListeners?: SyntheticListenerMap;
  dragAttributes?: DraggableAttributes;
}

const TabBodyRowRevamp = ({
  activity,
  position,
  type,
  sequence_id,
  customRoutinePosition,
  dragListeners,
  dragAttributes
}: TabBodyRowRevampProps) => {
  const dispatch = useAppDispatch();
  const [isHoveringAdd, setIsHoveringAdd] = useState<boolean>(false);

  const duration = activity.duration_seconds || 0;
  const emoji = activity.habit_icon || EMPTY_STRING;
  const habitName = activity.name || '';
  const isFromHabitPack = Boolean(activity.activity_template_id);
  const habitNameInputRef = useRef<HTMLTextAreaElement>(null);
  const handleDelete = useConfirmSettingsDeletion({
    target: SETTINGS_DELETE_TARGET.HABIT,
    name: habitName,
    type,
    position,
    customRoutinePosition
  });

  const { handleModeChange, handleDurationChange, handleCompletionChange } =
    useHabitRowTimerMode({
      activity,
      type,
      position,
      customRoutinePosition
    });

  useEffect(() => {
    if (!habitName && !isFromHabitPack) {
      habitNameInputRef.current?.focus();
    }
  }, [habitName, isFromHabitPack]);

  const fieldErrorKeys = useMemo(
    () => getActivityHabitFieldErrorKeys(activity),
    [activity]
  );

  const handleEmojiChange = (newEmoji: string) => {
    dispatch(
      updateSettingFeatures({
        type,
        position,
        feature: HABIT_SETTING_FEATURES.EMOJI,
        value: newEmoji,
        customRoutinePosition
      })
    );
  };

  const handleNameChange = (newName: string) => {
    dispatch(
      updateSettingFeatures({
        type,
        position,
        feature: HABIT_SETTING_FEATURES.NAME,
        value: newName,
        customRoutinePosition
      })
    );
  };

  const handleAddHabit = () => {
    dispatch(
      insertNewHabit({
        type,
        sequence_id,
        position: increment(position),
        category: activity.category,
        customRoutinePosition
      })
    );
  };

  const handleMoreOptions = () => {
    dispatch(updateMoreOptionActivity(activity));
    dispatch(updateMoreOptions({ type, position }));
    dispatch(updateMoreOptionActiveTab(MORE_OPTION_REVAMP_TAB.TIMING));
    dispatch(updateIsShowMoreOptionsOpened(true));
  };

  const handleLockedEditAttempt = () => {
    dispatch(
      openUnlockHabitPackModal({
        type,
        position,
        customRoutinePosition
      })
    );
  };

  const handleSuggestionSelect = (suggestion: ExtendedSuggestion) => {
    if (suggestion.where === SUGGESTED_PACK.CREATE_NEW_HABIT) {
      dispatch(
        updateSettingFeatures({
          type,
          position,
          feature: HABIT_SETTING_FEATURES.NAME,
          value: suggestion.name,
          customRoutinePosition
        })
      );
      return;
    }

    if (!suggestion.activity) {
      return;
    }

    dispatch(
      cloneHabit({
        templateActivity: suggestion.activity,
        newActivityPosition: position,
        type,
        customRoutinePosition
      })
    );
  };

  return (
    <div className='flex flex-col' {...getHabitRowDataProps(activity.id)}>
      <Container as='div' className='relative flex' variant='none'>
        {dragListeners && dragAttributes && (
          <div {...dragListeners} {...dragAttributes} className='inline-flex'>
            <BaseButton
              icon={
                <Draggable
                  className='w-auto ~h-4/5'
                  fill={COLOR.PRIMARY.LIGHT_900}
                  fillDarkMode={COLOR.PRIMARY.DARK_500}
                />
              }
              aria-label={t('habit_actions_aria_labels.reorder', {
                habit: getHabitAccessibleName(habitName)
              })}
              data-testid='drag-handle'
              variant='icon'
            />
          </div>
        )}
        <Card size='large' className='flex flex-col'>
          <div className='w-full flex flex-col md:flex-row ~gap-5/10'>
            <HabitNameInput
              emoji={emoji}
              habitName={habitName}
              onEmojiChange={handleEmojiChange}
              onNameChange={handleNameChange}
              onSuggestionSelect={
                isFromHabitPack ? undefined : handleSuggestionSelect
              }
              activityType={type}
              onLockedInteraction={
                isFromHabitPack ? handleLockedEditAttempt : undefined
              }
              readOnly={isFromHabitPack}
              inputRef={habitNameInputRef}
              hasError={Boolean(fieldErrorKeys.name)}
            />
            <HabitDurationCompletion
              onModeChange={handleModeChange}
              durationSeconds={duration}
              onDurationChange={handleDurationChange}
              completionValue={activity.completion_requirements || ''}
              onCompletionChange={handleCompletionChange}
              onLockedInteraction={
                isFromHabitPack ? handleLockedEditAttempt : undefined
              }
              areOptionsLocked={isFromHabitPack}
              shouldUseTimer={!hasCompletionRequirement(activity)}
              durationError={
                fieldErrorKeys.duration ? t(fieldErrorKeys.duration) : undefined
              }
              completionError={
                fieldErrorKeys.completion
                  ? t(fieldErrorKeys.completion)
                  : undefined
              }
            />
          </div>
          {isFromHabitPack && (
            <Banner
              message={t('settings_page.habit_pack_activity_locked_warning')}
              variant='warning'
              size='small'
              className='~mt-2/3'
            />
          )}
          <HabitFeatureTags activity={activity} className='~mt-2/3' />
          <HabitActionBar
            onAddHabit={handleAddHabit}
            onMoreOptions={isFromHabitPack ? undefined : handleMoreOptions}
            onDelete={isFromHabitPack ? undefined : handleDelete}
            onAddHover={setIsHoveringAdd}
            habitName={habitName}
            className='flex items-center justify-between ~gap-2/3 ~mt-2/3'
          />
        </Card>
      </Container>
      <InsertionIndicator
        visible={isHoveringAdd}
        className='!w-9/12 md:!w-11/12 self-center !~mt-5/10 !~mb-1/4'
        onClick={handleAddHabit}
      />
    </div>
  );
};

export default TabBodyRowRevamp;
